import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { SortOrder, SortAs } from '@ocean/types/dist/sort'
import { LoggerUtils, SortUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getUserNotifications,
  markUserNotificationsAsRead,
} from 'modules/Notifications/api/notificationsService'
import {
  SeverityFilter,
  NotificationItem,
  EventNotificationItem,
  SeverityLevel,
} from 'modules/Notifications/types'
import {
  NotificationsState,
  ApiHighlightedAndEventNotificationsResponse,
  ApiUserNotificationsResponse,
  ApiNotificationsMarkResponse,
} from './types'

const initialState: NotificationsState = {
  userNotifications: {},
  highlightedAndEventNotifications: {},
  highlightedNotifications: null,
  highlightedViewed: false,
  eventNotifications: null,
  eventViewed: false,
  maintenanceViewed: null,
  notificationMarked: {},
  bannerViewed: null,
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    notificationsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiUserNotificationsResponse>>
    ) {
      state.userNotifications = action.payload
    },
    highlightedAndEventNotificationsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiHighlightedAndEventNotificationsResponse>>
    ) {
      const { error, isLoading } = action.payload
      state.highlightedAndEventNotifications = { isLoading, error }
    },
    highlightedNotificationsLoaded(state, action: PayloadAction<NotificationItem[]>) {
      state.highlightedNotifications = action.payload
    },
    setHighlightedViewedState(state, action: PayloadAction<boolean>) {
      state.highlightedViewed = action.payload
    },
    eventNotificationsLoaded(state, action: PayloadAction<EventNotificationItem[]>) {
      state.eventNotifications = action.payload
    },
    setEventViewedState(state, action: PayloadAction<boolean>) {
      state.eventViewed = action.payload
    },
    setMaintenanceViewedState(state, action: PayloadAction<boolean>) {
      state.maintenanceViewed = action.payload
    },
    setBannerViewedState(state, action: PayloadAction<boolean>) {
      state.bannerViewed = action.payload
    },
    notificationMarkProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiNotificationsMarkResponse>>
    ) {
      state.notificationMarked = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  notificationsLoaded,
  highlightedAndEventNotificationsLoaded,
  highlightedNotificationsLoaded,
  setHighlightedViewedState,
  eventNotificationsLoaded,
  setEventViewedState,
  setMaintenanceViewedState,
  setBannerViewedState,
  notificationMarkProcessed,
} = notificationsSlice.actions

export {
  setHighlightedViewedState,
  setEventViewedState,
  setMaintenanceViewedState,
  setBannerViewedState,
}

export default notificationsSlice.reducer

export const loadUserNotificationsData = () => {
  return createApiAction({
    apiCalls: {
      notifications: () => getUserNotifications(),
    },
    payloadAction: notificationsLoaded,
  })
}

const splitNotifications =
  (response: ApiMiddlewarePayload<ApiHighlightedAndEventNotificationsResponse>): AppThunk =>
  async dispatch => {
    const { result, error } = response

    if (error) LoggerUtils.error('Highlighted and event notifications error: ', error?.error)

    if (result) {
      const highlighted = result.highlightedAndEventNotifications?.userNotifications?.filter(
        ({ severity }) => severity === SeverityLevel.Highlighted
      )
      const event = result.highlightedAndEventNotifications?.userNotifications
        ?.filter(({ severity }) => severity === SeverityLevel.Event)
        .sort(SortUtils.sortByMultipleKeys(['deadline'], SortOrder.Ascending, SortAs.Date))

      dispatch(highlightedNotificationsLoaded(highlighted))
      dispatch(eventNotificationsLoaded(event as EventNotificationItem[]))
    }
    dispatch(highlightedAndEventNotificationsLoaded(response))
  }

export const loadUserHighlightedAndEventNotificationsData = () => {
  return createApiAction({
    apiCalls: {
      highlightedAndEventNotifications: () =>
        getUserNotifications([SeverityFilter.Highlighted, SeverityFilter.Event]),
    },
    payloadAction: highlightedAndEventNotificationsLoaded,
    nextAction: splitNotifications,
    shouldCallApi: ({ notifications }) => !notifications.highlightedAndEventNotifications.isLoading,
  })
}

export const markNotificationAsRead = (id: Guid) => {
  return createApiAction({
    apiCalls: {
      notificationMark: () => markUserNotificationsAsRead(id),
    },
    payloadAction: notificationMarkProcessed,
  })
}
